import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Neighborhood } from "../../types/neighborhood";
import { NeighborhoodSelectorContent } from "../components/chat/NeighborhoodSelectorContent";
import { Button } from "../components/ui/button";
import { fetchServiceAreaPlaces } from "../lib/fetchServiceAreaPlaces";
import {
  neighborhoodKey,
  neighborhoodStorageName,
} from "../lib/neighborhoodDisplay";
import {
  serviceAreaSearchCenterFromNeighborhoods,
  type ServiceAreaSearchCenter,
} from "../lib/serviceAreaSearchCenter";
import { useDebouncedValue } from "../lib/useDebouncedValue";

export type UseNeighborhoodSelectorResult = {
  content: React.ReactNode;
  bottomBar: React.ReactNode;
};

export type InitialSelection = "all" | "none" | string[];

export type NeighborhoodPlaceSearchConfig = {
  placesUrl: string;
  csrfToken: string;
  googleMapsApiKey?: string;
  center?: ServiceAreaSearchCenter | null;
  country?: string;
};

/** Stable empty list for callers when the selector is inactive. */
export const EMPTY_NEIGHBORHOODS: Neighborhood[] = [];

const PLACE_SEARCH_DEBOUNCE_MS = 350;
const MIN_PLACE_SEARCH_LENGTH = 2;

function buildInitialSelected(
  initialSelection: InitialSelection,
  neighborhoods: Neighborhood[],
): Set<string> {
  if (initialSelection === "none") return new Set<string>();
  if (Array.isArray(initialSelection)) {
    const byName = new Map(
      neighborhoods.map((n) => [
        n.name.trim().toLowerCase(),
        neighborhoodKey(n),
      ]),
    );
    return new Set(
      initialSelection
        .map((name) => byName.get(name.trim().toLowerCase()))
        .filter((key): key is string => key != null),
    );
  }
  return new Set(neighborhoods.map((n) => neighborhoodKey(n)));
}

function mergeBrowseNeighborhoods(
  neighborhoods: Neighborhood[],
  addedNeighborhoods: Neighborhood[],
): Neighborhood[] {
  const seen = new Set<string>();
  const merged: Neighborhood[] = [];
  for (const n of [...neighborhoods, ...addedNeighborhoods]) {
    const key = neighborhoodKey(n);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(n);
  }
  return merged;
}

export function useNeighborhoodSelector(
  city: string,
  neighborhoods: Neighborhood[],
  onConfirm: (selected: Neighborhood[]) => void,
  onCancel: () => void,
  initialSelection: InitialSelection = "none",
  placeSearch?: NeighborhoodPlaceSearchConfig,
): UseNeighborhoodSelectorResult {
  const [selected, setSelected] = useState<Set<string>>(() =>
    buildInitialSelected(initialSelection, neighborhoods),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [addedNeighborhoods, setAddedNeighborhoods] = useState<Neighborhood[]>(
    [],
  );
  const [remoteResults, setRemoteResults] = useState<Neighborhood[]>([]);
  const [remoteSearchLoading, setRemoteSearchLoading] = useState(false);
  const [remoteSearchFailed, setRemoteSearchFailed] = useState(false);
  const searchRequestIdRef = useRef(0);

  const debouncedSearchQuery = useDebouncedValue(
    searchQuery.trim(),
    PLACE_SEARCH_DEBOUNCE_MS,
  );

  const isSearchActive = searchQuery.trim().length >= MIN_PLACE_SEARCH_LENGTH;
  const debouncedSearchActive =
    debouncedSearchQuery.length >= MIN_PLACE_SEARCH_LENGTH;
  const searchResultsPending =
    isSearchActive && debouncedSearchQuery !== searchQuery.trim();

  const namesKey = useMemo(
    () =>
      neighborhoods
        .map((n) => n.name)
        .sort()
        .join(","),
    [neighborhoods],
  );

  const placeSearchPlacesUrl = placeSearch?.placesUrl;
  const placeSearchCsrfToken = placeSearch?.csrfToken ?? "";
  const placeSearchGoogleMapsApiKey = placeSearch?.googleMapsApiKey;
  const placeSearchCountry = placeSearch?.country;

  const searchCenter = useMemo(
    () =>
      serviceAreaSearchCenterFromNeighborhoods(
        neighborhoods,
        placeSearch?.center ?? null,
      ),
    [neighborhoods, placeSearch?.center],
  );

  const searchCenterKey = searchCenter
    ? `${searchCenter.lat},${searchCenter.lng}`
    : "";

  const catalogByKey = useMemo(() => {
    const map = new Map<string, Neighborhood>();
    neighborhoods.forEach((n) => map.set(neighborhoodKey(n), n));
    addedNeighborhoods.forEach((n) => map.set(neighborhoodKey(n), n));
    return map;
  }, [neighborhoods, addedNeighborhoods]);

  const excludeNamesRef = useRef<string[]>([]);
  excludeNamesRef.current = [...catalogByKey.values()].map((n) => n.name);

  const initialSelectionKey = Array.isArray(initialSelection)
    ? initialSelection.join("\0")
    : initialSelection;

  const prevNamesKeyRef = useRef("");
  useEffect(() => {
    if (neighborhoods.length > 0 && namesKey !== prevNamesKeyRef.current) {
      prevNamesKeyRef.current = namesKey;
      setSelected(buildInitialSelected(initialSelection, neighborhoods));
      setSearchQuery("");
      setAddedNeighborhoods([]);
      setRemoteResults([]);
      setRemoteSearchLoading(false);
      setRemoteSearchFailed(false);
    }
  }, [namesKey, neighborhoods, initialSelection, initialSelectionKey]);

  useEffect(() => {
    if (!debouncedSearchActive) {
      setRemoteResults([]);
      setRemoteSearchLoading(false);
      setRemoteSearchFailed(false);
      return;
    }

    if (!placeSearchPlacesUrl && !placeSearchGoogleMapsApiKey) {
      setRemoteResults([]);
      setRemoteSearchLoading(false);
      setRemoteSearchFailed(true);
      return;
    }

    const controller = new AbortController();
    const requestId = ++searchRequestIdRef.current;
    setRemoteSearchLoading(true);
    setRemoteSearchFailed(false);

    fetchServiceAreaPlaces({
      placesUrl: placeSearchPlacesUrl ?? "",
      csrfToken: placeSearchCsrfToken,
      googleMapsApiKey: placeSearchGoogleMapsApiKey,
      query: debouncedSearchQuery,
      center: searchCenter,
      country: placeSearchCountry,
      excludeNames: excludeNamesRef.current,
      signal: controller.signal,
    })
      .then((places) => {
        if (searchRequestIdRef.current !== requestId) return;
        setRemoteResults(places);
        setRemoteSearchFailed(false);
      })
      .catch((error: unknown) => {
        if (searchRequestIdRef.current !== requestId) return;
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setRemoteResults([]);
        setRemoteSearchFailed(true);
      })
      .finally(() => {
        if (searchRequestIdRef.current === requestId) {
          setRemoteSearchLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [
    debouncedSearchQuery,
    debouncedSearchActive,
    placeSearchPlacesUrl,
    placeSearchCsrfToken,
    placeSearchGoogleMapsApiKey,
    placeSearchCountry,
    searchCenterKey,
  ]);

  const browseNeighborhoods = useMemo(
    () => mergeBrowseNeighborhoods(neighborhoods, addedNeighborhoods),
    [neighborhoods, addedNeighborhoods],
  );

  const toggleNeighborhood = useCallback(
    (neighborhood: Neighborhood) => {
      const key = neighborhoodKey(neighborhood);
      const inBase = neighborhoods.some((n) => neighborhoodKey(n) === key);
      if (!inBase) {
        setAddedNeighborhoods((prev) => {
          if (prev.some((n) => neighborhoodKey(n) === key)) {
            return prev;
          }
          return [...prev, neighborhood];
        });
      }
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    [neighborhoods],
  );

  const selectAll = useCallback(
    () => setSelected(new Set(neighborhoods.map((n) => neighborhoodKey(n)))),
    [neighborhoods],
  );

  const handleConfirm = useCallback(() => {
    const selectedNeighborhoods = [...selected]
      .map((key) => catalogByKey.get(key))
      .filter((n): n is Neighborhood => n != null)
      .map((n) =>
        n.regionLabel ? { ...n, name: neighborhoodStorageName(n) } : n,
      );
    onConfirm(selectedNeighborhoods);
  }, [catalogByKey, onConfirm, selected]);

  const content = (
    <NeighborhoodSelectorContent
      city={city}
      browseNeighborhoods={browseNeighborhoods}
      searchNeighborhoods={remoteResults}
      isSearchActive={isSearchActive}
      selected={selected}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      onToggle={toggleNeighborhood}
      onSelectAll={selectAll}
      remoteSearchLoading={remoteSearchLoading || searchResultsPending}
      remoteSearchFailed={remoteSearchFailed}
    />
  );

  const bottomBar = (
    <>
      <span className="flex-1 text-sm text-muted-foreground">
        {selected.size} area{selected.size === 1 ? "" : "s"} selected
      </span>
      <Button type="button" variant="outline" onClick={onCancel}>
        Skip
      </Button>
      <Button
        type="button"
        variant="premium"
        onClick={handleConfirm}
        disabled={selected.size === 0}
      >
        Confirm Selection
      </Button>
    </>
  );

  return { content, bottomBar };
}
